import React, { useEffect, useState } from 'react';
import { z, ZodSchema } from 'zod';
import stateCodeToName from '@/constants/stateCodeToName';
import type { USAddress } from '@/typings/api-models';
import { Input } from '../Input';
import Select from '../Select';
import Fieldset from '../shared/Fieldset';
import AddressUSAutocomplete from '../../AddressUSAutocomplete';
import s from './AddressUS.module.scss';
import Button from '../../Button';

export const addressSchema = (required: boolean): ZodSchema =>
  z.object({
    city: required ? z.string().nonempty('City is required') : z.string().optional(),
    line1: required ? z.string().nonempty('Street Address is required') : z.string(),
    line2: z.string().optional(),
    state: required ? z.string().nonempty('State is required') : z.string().optional(),
    zip: required
      ? z
        .string()
        .nonempty('Zip is required')
        .regex(/^\d{5}$/, 'Zip code must be exactly 5 digits')
      : z
        .string()
        .regex(/^\d{5}$/, 'Zip code must be exactly 5 digits')
        .optional(),
  });

export const DEFAULT_VALUE: USAddress = {
  city: '',
  line1: '',
  line2: '',
  state: '',
  zip: '',
};

type AutoCompleteAddress = Partial<Omit<USAddress, 'line2'>>;

const AddressUS: React.FC<
  Pick<Parameters<typeof Fieldset>[0], 'label' | 'description' | 'required'> & {
    value: USAddress | null;
    onChange: (address: USAddress) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    disabled?: boolean;
    required?: boolean;
  } & {
    error?: string;
  }
> = ({ label, description, disabled, required = true, value, onChange, onBlur, onFocus, error }) => {
  const [address, setAddress] = useState(value || DEFAULT_VALUE);
  const [hasPartialFields, setHasPartialFields] = useState<boolean>(false);

  const isOptional = required ? false : !hasPartialFields;

  const handleInputChange = (field: 'line2' | 'city' | 'state' | 'zip') => (v: string) => {
    const newAddress = { ...address, [field]: v };

    setAddress(newAddress);
    onChange(newAddress);
  };

  const cleanAddress = (addr: AutoCompleteAddress | null): AutoCompleteAddress =>
    Object.fromEntries(Object.entries(addr || {}).filter(([_, v]) => v !== ''));

  const handleAutocompleteChange = (newAddress: AutoCompleteAddress | null): void => {
    const cleanedAddress = cleanAddress(newAddress);
    const updatedAddress = {
      city: cleanedAddress.city ?? address.city,
      line1: cleanedAddress.line1 ?? address.line1,
      line2: address.line2,
      state: cleanedAddress.state ?? address.state,
      zip: cleanedAddress.zip ?? address.zip,
    };
    setAddress(updatedAddress);
    onChange(updatedAddress);
  };

  useEffect(() => {
    if (value) {
      setAddress(value);
    }
  }, [value]);

  useEffect(() => {
    const checkPartialFields = (): void => {
      const { city, state, zip, line1 } = address;

      const isAnyFieldFilled = !!(city || state || zip || line1);

      const areAllFieldsFilled = !!(city && state && zip && line1);

      setHasPartialFields(!required && isAnyFieldFilled && !areAllFieldsFilled);
    };

    checkPartialFields();
  }, [address, required]);

  return (
    <Fieldset label={label} description={description} required={required} error={error}>
      <div className={s['address-us']}>
        {address !== DEFAULT_VALUE && (
          <Button
            variant="secondary"
            className={s.clear}
            size="xs"
            onClick={() => {
              setAddress(DEFAULT_VALUE);
              onChange(DEFAULT_VALUE);
            }}
          >
            Clear
          </Button>
        )}
        <AddressUSAutocomplete
          value={address.line1}
          onFocus={onFocus}
          onAddressChange={handleAutocompleteChange}
          disabled={disabled}
          optional={isOptional}
          onBlur={onBlur}
        />
        <Input
          label="Street Address Line 2"
          compact
          value={address.line2}
          onChange={handleInputChange('line2')}
          placeholder="Apartment, suite, unit, building, floor, etc."
          optional={true}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        <div className={s.row}>
          <Input
            label="City"
            compact
            value={address.city}
            onChange={handleInputChange('city')}
            placeholder="Enter a city"
            optional={isOptional}
            onBlur={onBlur}
            disabled={disabled}
            onFocus={onFocus}
          />
          <Select
            label="State"
            compact
            placeholder="Select"
            options={stateCodeToName}
            value={address.state}
            optional={isOptional}
            onChange={handleInputChange('state')}
            disabled={disabled}
            onFocus={onFocus}
          />
          <Input
            label="Zip"
            compact
            value={address.zip}
            onChange={handleInputChange('zip')}
            placeholder="XXXXX"
            optional={isOptional}
            disabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        </div>
      </div>
    </Fieldset>
  );
};

export default AddressUS;
